import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  of,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { CMSDiscountCode, ConfigService, MonitoringLogger } from '@fcom/core/services';
import { AvailabilityEntry, Duration, FinnairBoundItem, LowestPriceOfPeriod } from '@fcom/dapi/api/models';
import { finShare, retryWithBackoff } from '@fcom/rx';
import {
  AppState,
  GlobalBookingTravelClass,
  isPresent,
  LocalDate,
  mapErrorForMonitoring,
  unsubscribe,
} from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { isFlight } from '@fcom/common-booking/utils/flight.utils';
import { DatePickerPrices, ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { Amount } from '@fcom/dapi/interfaces';
import { Location } from '@fcom/core-api/interfaces';
import { CmsTripTypeMap } from '@fcom/core/utils/tripType.utils';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import {
  getPricesForPeriod,
  getPricesForPeriodWithFixedDepartureDate,
  getTimetableForRoute,
  GetTimetableForRoute$Params,
} from '@fcom/dapi/api/instantsearch-functions';

import {
  CalendarPrices,
  GlobalBookingTripDates,
  HistogramBar,
  InstantSearchBaseParams,
  InstantSearchFollowingMonthParams,
  InstantSearchFullYearParams,
  PriceCalendarCTAParams,
  PriceCalendarParams,
  PriceCalendarWithPricesParams,
  TripType,
} from '../../interfaces';
import {
  createHistogramData,
  getBookingWidgetCabinClass,
  getLowestCalendarPrice,
  getStartingFromPrice,
  isPriceCalendarWithPricesParams,
  mapInstantSearchPricesToCalendarPrices,
  priceCalendarBaseParamsAreValid,
  priceCalendarPriceParamsAreValid,
} from '../../utils';
import { GlobalBookingActions, selectGlobalBookingDiscountCode, GlobalBookingFlight } from '../../store';
import { GtmService } from '../../gtm';
import { createDeeplinkPathFromFlightSearchParams } from '../../utils/flight-search.utils';

const DAYS_YEAR = 360;
export const DAYS_MONTH = 30;

export interface ScheduleBoundsState {
  loading: boolean;
  bounds: FinnairBoundItem[];
}

export interface DestinationRouteInfo {
  tripType: TripType;
  startingFromPrice?: Amount;
  duration?: Duration;
  airportCodes: string[];
  isDirect: boolean;
}

@Injectable()
export class PriceCalendarService implements OnDestroy {
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);
  private dapiService = inject(DapiService);
  private monitoringLogger = inject(MonitoringLogger);
  private appStore$ = inject<Store<AppState>>(Store);
  private gtmService = inject(GtmService);
  private router = inject(Router);

  static NUMBER_OF_RETRIES = 2;

  private readonly _startingFromPrice$: BehaviorSubject<Amount> = new BehaviorSubject(undefined);
  private readonly _fullYearPrices$: BehaviorSubject<DatePickerPrices> = new BehaviorSubject({
    calendar: undefined,
    histogram: undefined,
  });
  private readonly _calendarPrices$: Observable<DatePickerPrices> = of(undefined);
  private _followingMonthPrices$: BehaviorSubject<CalendarPrices> = new BehaviorSubject({});
  private _travelDates$ = new BehaviorSubject<GlobalBookingTripDates>({
    departureDate: undefined,
    returnDate: undefined,
  });

  private _params$: BehaviorSubject<PriceCalendarParams> = new BehaviorSubject<PriceCalendarParams>(undefined);

  private _isLoading$: Observable<boolean>;

  private _discountCode$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private _availability$: BehaviorSubject<AvailabilityEntry[]> = new BehaviorSubject<AvailabilityEntry[]>([]);

  private _scheduleBounds$: Observable<ScheduleBoundsState>;
  private _returnScheduleBounds$: Observable<ScheduleBoundsState>;

  private subscriptions = new Subscription();

  private getPricesForFullYearRequestsCache$ = new BehaviorSubject<
    Map<
      string,
      Observable<{
        fullYear: CalendarPrices;
        histogram: HistogramBar[];
        availability?: AvailabilityEntry[];
      }>
    >
  >(new Map());
  service: Observable<PriceCalendarWithPricesParams>;
  calendarOpen$ = new Subject<PriceCalendarParams | null>();

  constructor() {
    this._calendarPrices$ = combineLatest([
      this._params$.pipe(filter(isPresent)),
      this._fullYearPrices$,
      this._followingMonthPrices$,
      this._travelDates$,
    ]).pipe(
      map(([{ tripType }, fullYearPrices, followingMonth, { departureDate, returnDate }]) => {
        const oneway = tripType === TripType.ONEWAY;
        const onlyDepartureSelectedAndNotOneway = isPresent(departureDate) && !oneway && !isPresent(returnDate);
        const prices = onlyDepartureSelectedAndNotOneway ? followingMonth : fullYearPrices?.calendar;

        const pricesWithSelections =
          departureDate && returnDate ? { ...prices, [returnDate?.id]: followingMonth?.[returnDate?.id] } : prices;
        return { calendar: pricesWithSelections, histogram: fullYearPrices?.histogram };
      }),
      finShare()
    );

    this.subscriptions.add(
      combineLatest([this._calendarPrices$, this._params$.pipe(filter(isPresent)), this._travelDates$])
        .pipe(
          map(([prices, { tripType }, travelDates]) => getStartingFromPrice(prices, tripType, travelDates)),
          startWith(undefined)
        )
        .subscribe((val) => this._startingFromPrice$.next(val))
    );

    this.subscriptions.add(
      this._params$
        .pipe(
          filter(isPresent),
          filter((params) => isPriceCalendarWithPricesParams(params)),
          distinctUntilChanged(
            (prev: PriceCalendarWithPricesParams, next: PriceCalendarWithPricesParams) =>
              prev.origin === next.origin &&
              prev.destination === next.destination &&
              prev.tripType === next.tripType &&
              prev.travelClass === next.travelClass &&
              prev.locale === next.locale
          ),
          switchMap((params: PriceCalendarWithPricesParams) => this.getPricesForFullYear(params)),
          map(({ fullYear, histogram, availability }) => [{ calendar: fullYear, histogram }, availability] as const),
          finShare()
        )
        .subscribe(([val, availability]) => {
          this._fullYearPrices$.next(val);
          this._availability$.next(availability);
        })
    );

    this.subscriptions.add(
      combineLatest([this._params$, this._travelDates$])
        .pipe(
          filter(
            ([params, travelDates]: [PriceCalendarWithPricesParams, GlobalBookingTripDates]) =>
              isPresent(params) && isPresent(travelDates.departureDate?.id)
          ),
          distinctUntilChanged(([prevParams, prevTravelDates], [nextParams, nextTravelDates]) => {
            const departureDateNotChanged = nextTravelDates.departureDate?.id === prevTravelDates.departureDate?.id;
            const locationsNotChanged =
              nextParams.origin === prevParams.origin && nextParams.destination === prevParams.destination;
            const tripTypeNotChanged = prevParams.tripType === nextParams.tripType;
            const travelClassNotChanged = prevParams.travelClass === nextParams.travelClass;
            const localeNotChanged = prevParams.locale === nextParams.locale;
            return (
              departureDateNotChanged &&
              locationsNotChanged &&
              tripTypeNotChanged &&
              travelClassNotChanged &&
              localeNotChanged
            );
          }),
          filter(([params]) => isPriceCalendarWithPricesParams(params)),
          switchMap(([params, travelDates]: [PriceCalendarWithPricesParams, GlobalBookingTripDates]) =>
            this.getPricesForFollowingMonth(params, travelDates.departureDate)
          ),
          finShare()
        )
        .subscribe((value) => this._followingMonthPrices$.next(value))
    );

    this.subscriptions.add(
      this.appStore$
        .select(selectGlobalBookingDiscountCode)
        .pipe(map((discountCode: string) => (isPresent(discountCode) ? discountCode : null)))
        .subscribe((discountCode: string) => {
          this._discountCode$.next(discountCode);
        })
    );

    this._isLoading$ = this.getPricesForFullYearRequestsCache$.pipe(
      map((cache) => cache.size > 0),
      finShare()
    );

    this.subscriptions.add(
      this._isLoading$.pipe(filter(Boolean)).subscribe(() => {
        this._availability$.next([]);
      })
    );

    // Timetable bounds per leg. Derived from the same params$/travelDates$ this service already
    // owns, and fetched via the shared instant-search DAPI
    this._scheduleBounds$ = this.buildScheduleBounds$((params, dates) => ({
      origin: params?.origin,
      destination: params?.destination,
      date: dates?.departureDate,
    }));

    this._returnScheduleBounds$ = this.buildScheduleBounds$((params, dates) => ({
      origin: params?.destination,
      destination: params?.origin,
      date: dates?.returnDate,
    }));
  }

  /**
   * Builds a schedule-bounds stream for one leg. `legSelector` picks the origin/destination/date
   * from the current params + travel dates. Emits an empty state until all three are
   * present, then fetches the day's flights and keeps only those departing on the selected date.
   */
  private buildScheduleBounds$(
    legSelector: (
      params: PriceCalendarParams,
      dates: GlobalBookingTripDates
    ) => { origin: string | undefined; destination: string | undefined; date: LocalDate | undefined }
  ): Observable<ScheduleBoundsState> {
    return combineLatest([this._params$, this._travelDates$]).pipe(
      map(([params, dates]) => legSelector(params, dates)),
      distinctUntilChanged(
        (prev, next) =>
          prev.origin === next.origin && prev.destination === next.destination && prev.date?.id === next.date?.id
      ),
      switchMap(({ origin, destination, date }) => {
        if (!isPresent(origin) || !isPresent(destination) || !isPresent(date)) {
          return of<ScheduleBoundsState>({ loading: false, bounds: [] });
        }
        const selectedDate = date.toString();
        const requestParams: GetTimetableForRoute$Params = {
          originLocationCode: origin,
          destinationLocationCode: destination,
          departureDate: selectedDate,
          numberOfDays: 1,
          directFlights: false,
        };
        return this.dapiService
          .invoke$(getTimetableForRoute, this.configService.cfg.instantSearchUrl, requestParams)
          .pipe(
            map((timetable): ScheduleBoundsState => ({
              loading: false,
              bounds: (timetable?.flights ?? []).filter((bound) => bound.departure.dateTime.startsWith(selectedDate)),
            })),
            catchError(() => of<ScheduleBoundsState>({ loading: false, bounds: [] })),
            startWith<ScheduleBoundsState>({ loading: true, bounds: [] })
          );
      }),
      finShare()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  updateParams(params: PriceCalendarParams): void {
    if (isPresent(params) && (priceCalendarPriceParamsAreValid(params) || priceCalendarBaseParamsAreValid(params))) {
      this._params$.next(params);
      if (params.initialTravelDates) {
        this._travelDates$.next(params.initialTravelDates);
      }
    }
  }

  get params$(): Observable<PriceCalendarParams> {
    return this._params$.asObservable().pipe(finShare());
  }

  get tripType$(): Observable<TripType> {
    return this._params$.pipe(
      filter(isPresent),
      map(({ tripType }) => tripType),
      finShare()
    );
  }

  get travelDates$(): Observable<GlobalBookingTripDates> {
    return this._travelDates$.asObservable().pipe(finShare());
  }

  get travelDatesValues(): GlobalBookingTripDates {
    return this._travelDates$.getValue();
  }

  /** Timetable flights (+ loading) for the selected departure date */
  get scheduleBounds$(): Observable<ScheduleBoundsState> {
    return this._scheduleBounds$;
  }

  /** Timetable flights (+ loading) for the return leg */
  get returnScheduleBounds$(): Observable<ScheduleBoundsState> {
    return this._returnScheduleBounds$;
  }

  get startingFromPrice$(): Observable<Amount> {
    return this._startingFromPrice$.pipe(finShare());
  }

  get fullYearPrices$(): Observable<DatePickerPrices> {
    return this._fullYearPrices$.asObservable().pipe(finShare());
  }

  get calendarPrices$(): Observable<DatePickerPrices> {
    return this._calendarPrices$;
  }

  get isLoading$(): Observable<boolean> {
    return this._isLoading$.pipe(finShare());
  }

  get pricesAvailable$(): Observable<boolean> {
    return combineLatest([this.fullYearPrices$, this.isLoading$]).pipe(
      filter(([{ calendar }, isLoading]) => isPresent(calendar) && !isLoading),
      map(([{ calendar }]) => Object.keys(calendar).length > 0),
      distinctUntilChanged(),
      finShare()
    );
  }

  get pricesAvailableForSelectedDates$(): Observable<boolean> {
    return combineLatest([this.tripType$, this.travelDates$, this.pricesAvailable$, this.startingFromPrice$]).pipe(
      filter(([tripType, travelDates]) => {
        const { departureDate, returnDate } = travelDates;
        if (tripType === TripType.ONEWAY) {
          return isPresent(departureDate);
        }
        if (tripType === TripType.RETURN) {
          return isPresent(departureDate) && isPresent(returnDate);
        }
        return false;
      }),
      map(([, , pricesAvailable, startingFromPrice]) => pricesAvailable && isPresent(startingFromPrice)),
      finShare()
    );
  }

  get followingMonthPrices$(): Observable<CalendarPrices> {
    return this._followingMonthPrices$.asObservable().pipe(finShare());
  }

  get availability$(): Observable<AvailabilityEntry[]> {
    return this._availability$.asObservable().pipe(finShare());
  }

  convertToReturnTrip(): void {
    this._fullYearPrices$.next({ calendar: undefined, histogram: undefined });

    const currentParams = this._params$.getValue();
    this._params$.next({
      ...currentParams,
      tripType: TripType.RETURN,
    });
  }

  getPriceCalendarCTAParams(): Observable<PriceCalendarCTAParams> {
    return this._params$.pipe(
      filter(Boolean),
      take(1),
      map((params) => {
        const { tripType, destination, origin, discountCode } = params;

        const { departureDate, returnDate } = this._travelDates$.getValue();
        const departureFlight: GlobalBookingFlight = {
          origin: { locationCode: origin } as Location,
          destination: { locationCode: destination } as Location,
          departureDate,
        };
        const returnFlight: GlobalBookingFlight = {
          origin: { locationCode: destination } as Location,
          destination: { locationCode: origin } as Location,
          departureDate: returnDate,
        };

        const flights: GlobalBookingFlight[] =
          tripType === TripType.RETURN ? [departureFlight, returnFlight] : [departureFlight];

        return {
          flights,
          tripType: tripType,
          ...(isPriceCalendarWithPricesParams(params) && {
            paxAmount: params.paxAmount,
            travelClass: getBookingWidgetCabinClass(params.travelClass, false, false, tripType === TripType.MULTICITY),
          }),
          discountCode,
        };
      })
    );
  }

  getPricesForFullYear(params: PriceCalendarWithPricesParams): Observable<{
    fullYear: CalendarPrices;
    histogram: HistogramBar[];
    availability?: AvailabilityEntry[];
  }> {
    const fullYearParams = this.convertToFullYearParams(params);
    const serializedParams = JSON.stringify(fullYearParams);

    const requestCacheMap = this.getPricesForFullYearRequestsCache$.getValue();
    if (requestCacheMap.has(serializedParams)) {
      return requestCacheMap.get(serializedParams);
    }

    const request$ = this.dapiService
      .invoke$(getPricesForPeriod, this.configService.cfg.instantSearchUrl, fullYearParams)
      .pipe(
        withLatestFrom(this.languageService.translate<Record<string, string>>('date')),
        map(([response, dateTranslations]: [LowestPriceOfPeriod, Record<string, string>]) => {
          const fullYear = mapInstantSearchPricesToCalendarPrices(response);
          const histogram = createHistogramData(response, Object.keys(fullYear), dateTranslations);

          return {
            fullYear,
            histogram,
            availability: response?.availability,
          };
        }),
        retryWithBackoff(PriceCalendarService.NUMBER_OF_RETRIES),
        catchError((err: unknown) => {
          this.monitoringLogger.error('Error finding prices for period', {
            error: mapErrorForMonitoring(err),
          });
          return of({
            fullYear: {},
            histogram: [],
          });
        }),
        finalize(() => {
          const newMap = new Map(requestCacheMap);
          newMap.delete(serializedParams);
          this.getPricesForFullYearRequestsCache$.next(newMap);
        }),
        finShare()
      );
    const newMap = new Map(requestCacheMap);
    newMap.set(serializedParams, request$);
    this.getPricesForFullYearRequestsCache$.next(newMap);

    return request$;
  }

  /**
   * Fetches date-independent "general info" for a route based on full calendar year data
   * available from the instant-search DAPI.
   */
  getDestinationRouteInfo({
    origin,
    destination,
    tripType,
  }: {
    origin: string;
    destination: string;
    tripType: TripType;
  }): Observable<DestinationRouteInfo> {
    if (!origin || !destination) {
      return of({ tripType, airportCodes: [], isDirect: false });
    }

    const pricesParams: PriceCalendarWithPricesParams = {
      origin,
      destination,
      tripType,
      travelClass: GlobalBookingTravelClass.ECONOMY,
      paxAmount: { adults: 1, c15s: 0, children: 0, infants: 0 },
    };

    const startingFromPrice$: Observable<Amount | undefined> = this.getPricesForFullYear(pricesParams).pipe(
      map(({ fullYear }) => getLowestCalendarPrice(fullYear))
    );

    return combineLatest([startingFromPrice$, this.getRouteTimetable(origin, destination)]).pipe(
      map(([startingFromPrice, bounds]) => {
        const representativeBound = this.getRepresentativeBound(bounds);
        // Includes direct and w/ technical stop
        const isDirect = bounds.some((bound) => bound.itinerary.filter(isFlight).length === 1);
        return {
          tripType,
          startingFromPrice,
          duration: representativeBound?.duration,
          airportCodes: [...new Set(bounds.map((bound) => bound.arrival.locationCode))],
          isDirect,
        };
      }),
      finShare()
    );
  }

  private getRouteTimetable(origin: string, destination: string): Observable<FinnairBoundItem[]> {
    const params: GetTimetableForRoute$Params = {
      originLocationCode: origin,
      destinationLocationCode: destination,
      departureDate: LocalDate.now().toString(),
      numberOfDays: DAYS_MONTH,
      directFlights: false,
    };
    return this.dapiService.invoke$(getTimetableForRoute, this.configService.cfg.instantSearchUrl, params).pipe(
      map((timetable) => timetable?.flights ?? []),
      catchError(() => of<FinnairBoundItem[]>([]))
    );
  }

  private getRepresentativeBound(bounds: FinnairBoundItem[]): FinnairBoundItem | undefined {
    if (bounds.length === 0) {
      return undefined;
    }
    return (
      bounds.find((bound) => bound.stops === 0) ??
      bounds.reduce((shortest, bound) =>
        bound.duration.milliseconds < shortest.duration.milliseconds ? bound : shortest
      )
    );
  }

  getPricesForFollowingMonth(
    params: PriceCalendarWithPricesParams,
    departureDate: LocalDate
  ): Observable<CalendarPrices> {
    const followingMonthParams = this.convertToFollowingMonthParams(params, departureDate);
    return this.dapiService
      .invoke$(getPricesForPeriodWithFixedDepartureDate, this.configService.cfg.instantSearchUrl, followingMonthParams)
      .pipe(
        map((response: LowestPriceOfPeriod) => mapInstantSearchPricesToCalendarPrices(response)),
        retryWithBackoff(PriceCalendarService.NUMBER_OF_RETRIES),
        catchError(() => of({})),
        finShare()
      );
  }

  updateCalendarDates(calendarDates: GlobalBookingTripDates): void {
    this._travelDates$.next(calendarDates);
  }

  openPriceCalendar(
    originLocationCode: string,
    destinationLocationCode: string,
    tripType: TripType,
    travelClass: GlobalBookingTravelClass,
    destinationTitle: string,
    discountCode?: CMSDiscountCode,
    shouldTrackEvent = true
  ): void {
    this.calendarOpen$.next({
      origin: originLocationCode,
      destination: destinationLocationCode,
      tripType,
      travelClass,
      paxAmount: {
        adults: 1,
        c15s: 0,
        children: 0,
        infants: 0,
      },
      discountCode,
    });

    if (shouldTrackEvent) {
      this.gtmService.trackElement(
        `tile-${destinationTitle.replace(/ /g, '_')}-${travelClass}-${CmsTripTypeMap[tripType]}`,
        `${GaContext.DESTINATION_MAIN_PAGE}_open-dates`,
        ElementTypes.LIST_ITEM,
        undefined,
        ElementActions.CLICK
      );
    }
  }

  priceCalendarCTAClicked(params: PriceCalendarCTAParams): void {
    const discountCode = params.discountCode?.code ?? this._discountCode$.getValue();

    this.prefillBookingWidget(
      params.travelClass,
      { departureDate: params.flights[0].departureDate, returnDate: params.flights[1]?.departureDate },
      params.tripType,
      params.flights[0].destination.locationCode,
      params.flights[0].origin.locationCode,
      discountCode
    );

    const deeplink = createDeeplinkPathFromFlightSearchParams({
      flights: params.flights,
      tripType: params.tripType,
      paxAmount: params.paxAmount,
      travelClass: params.travelClass,
      locale: this.languageService.langValue,
      promoCode: discountCode,
    });
    this.router.navigateByUrl(deeplink);
  }

  closePriceCalendar(): void {
    this.calendarOpen$.next(null);
  }

  prefillBookingWidget(
    travelClass: GlobalBookingTravelClass,
    selectedDates: {
      departureDate: LocalDate | undefined;
      returnDate: LocalDate | undefined;
    },
    tripType: TripType,
    destinationLocationCode: string,
    selectedOriginLocationCode: string,
    discountCode?: string
  ): void {
    const departureFlight = {
      origin: { locationCode: selectedOriginLocationCode } as Location,
      destination: { locationCode: destinationLocationCode } as Location,
      departureDate: selectedDates.departureDate,
    };
    const returnFlight = {
      origin: { locationCode: destinationLocationCode } as Location,
      destination: { locationCode: selectedOriginLocationCode } as Location,
      departureDate: selectedDates.returnDate,
    };

    const flights: GlobalBookingFlight[] =
      tripType === TripType.RETURN ? [departureFlight, returnFlight] : [departureFlight];

    this.appStore$.dispatch(GlobalBookingActions.setFlights({ flights }));
    this.appStore$.dispatch(
      GlobalBookingActions.setTravelClass({
        travelClass: getBookingWidgetCabinClass(travelClass, false, false, tripType === TripType.MULTICITY),
      })
    );
    this.appStore$.dispatch(GlobalBookingActions.setOrigin({ origin: selectedOriginLocationCode }));
    this.appStore$.dispatch(GlobalBookingActions.setTripType({ tripType }));

    if (discountCode) {
      this.appStore$.dispatch(GlobalBookingActions.setDiscountCode({ discountCode }));
    }
  }

  private getCommonParams({
    origin,
    destination,
    paxAmount,
    travelClass,
  }: PriceCalendarWithPricesParams): Omit<InstantSearchBaseParams, 'numberOfDays'> {
    return {
      departureLocationCode: origin,
      destinationLocationCode: destination,
      adults: paxAmount.adults + paxAmount.c15s,
      children: paxAmount.children,
      infants: paxAmount.infants,
      locale: this.languageService.localeValue,
      travelClass: travelClass === GlobalBookingTravelClass.MIXED ? null : travelClass,
    };
  }

  private convertToFollowingMonthParams(
    params: PriceCalendarWithPricesParams,
    departureDate: LocalDate
  ): InstantSearchFollowingMonthParams {
    const commonParams = this.getCommonParams(params);
    return { ...commonParams, numberOfDays: DAYS_MONTH, departureDate: departureDate.id };
  }

  private convertToFullYearParams(params: PriceCalendarWithPricesParams): InstantSearchFullYearParams {
    const commonParams = this.getCommonParams(params);
    return {
      ...commonParams,
      oneway: params.tripType === TripType.ONEWAY,
      startDate: LocalDate.now().id,
      numberOfDays: DAYS_YEAR,
    };
  }
}
